import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleCheck, faCircleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicInputComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {
  AvailableSiteView,
  ConnectionSiteView,
  ExternalConnectionControllerService,
  SitePermissionSessionView,
  SiteResultView
} from '../../../../api';
import {NotificationsService} from '../../../../services/notifications/notifications.service';

export interface SitePermissionDialogData {
  organisationId: number;
  connectionId: string;
  session: SitePermissionSessionView;
  /** Aktuell freigegebene Sites; fehlen sie in der Microsoft-Suche, werden sie trotzdem angezeigt und bleiben ausgewählt. */
  grantedSites: ConnectionSiteView[];
}

/**
 * Sites freigeben (Figma Popup 3). Kommt nach Login 1 mit der Site-Liste aus Microsoft; die Auswahl ist der
 * Soll-Stand (bereits freigegebene sind vorausgewählt, abgewählte werden entfernt). „Änderungen übernehmen“
 * führt über Login 2 zu Microsoft und zurück. Zeigt außerdem das Ergebnis einer Sitzung mit Fehlern.
 */
@Component({
  selector: 'app-site-permission-dialog',
  templateUrl: './site-permission-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicPopupComponent, BicButtonComponent, BicInputComponent, FaIconComponent]
})
export class SitePermissionDialogComponent {
  readonly data = inject<SitePermissionDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<SitePermissionDialogComponent>>(MatDialogRef);
  private readonly connectionService = inject(ExternalConnectionControllerService);
  private readonly notifications = inject(NotificationsService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly session = this.data.session;
  readonly selecting = this.session.status === SitePermissionSessionView.StatusEnum.AWAITINGSELECTION;
  readonly sites: AvailableSiteView[] = SitePermissionDialogComponent.mergeSites(
    this.session.availableSites ?? [], this.data.grantedSites ?? []);
  readonly failedResults: SiteResultView[] = (this.session.results ?? []).filter(r => !r.ok);
  readonly okCount = (this.session.results ?? []).filter(r => r.ok).length;

  selected = new Set<string>(this.sites.filter(s => s.granted).map(s => s.siteId!));
  filteredSites: AvailableSiteView[] = this.sites;
  search = '';
  busy = false;

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;

  get changeCount(): number {
    return this.sites.filter(s => this.selected.has(s.siteId!) !== !!s.granted).length;
  }

  onSearch(value: string): void {
    this.search = value ?? '';
    const term = this.search.trim().toLowerCase();
    this.filteredSites = term
      ? this.sites.filter(s => (s.displayName ?? '').toLowerCase().includes(term) || (s.webUrl ?? '').toLowerCase().includes(term))
      : this.sites;
  }

  toggle(site: AvailableSiteView): void {
    if (this.selected.has(site.siteId!)) {
      this.selected.delete(site.siteId!);
    } else {
      this.selected.add(site.siteId!);
    }
  }

  apply(): void {
    this.busy = true;
    this.connectionService.submitSitePermissionSelection({siteIds: [...this.selected]}, this.data.organisationId,
      this.data.connectionId, this.session.id!).subscribe({
      next: start => this.redirect(start.loginUrl!),
      error: err => {
        this.busy = false;
        this.notifications.showErrorMessage(err?.error?.message ?? 'Die Auswahl konnte nicht gespeichert werden.');
        this.cdr.markForCheck();
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Freigegebene Sites, die Microsoft nicht mehr listet, gehören trotzdem in die Auswahl – sonst würden sie entfernt. */
  private static mergeSites(available: AvailableSiteView[], granted: ConnectionSiteView[]): AvailableSiteView[] {
    const listed = new Set(available.map(s => s.siteId));
    const missing = granted
      .filter(g => !listed.has(g.siteId))
      .map(g => ({siteId: g.siteId, displayName: g.displayName, webUrl: g.webUrl, granted: true}));
    return [...available, ...missing];
  }

  actionText(result: SiteResultView): string {
    return result.action === SiteResultView.ActionEnum.GRANT ? 'Freigeben' : 'Entfernen';
  }

  /** Verlässt planBIC Richtung Microsoft-Login; eigene Methode, damit Tests die Weiterleitung abfangen können. */
  redirect(url: string): void {
    window.location.assign(url);
  }
}
